import dayjs from "dayjs";

const startOfDay = (value) => {
  if (value == null) return null;
  const d = dayjs(value);
  if (!d.isValid()) return null;
  return d.startOf("day");
};

/** Today at start of day (calendar comparisons). */
export const dashboardTodayStart = () => dayjs().startOf("day");

/**
 * Clamp a picker value to a valid calendar day not after today.
 * @returns {Date|null} null when input is null/invalid
 */
export function clampDashboardPickerDate(value) {
  if (value == null) return null;
  const picked = startOfDay(value);
  if (!picked) return null;
  const today = dashboardTodayStart();
  return (picked.isAfter(today) ? today : picked).toDate();
}

/**
 * Apply a From/To change with same-day allowed, future disallowed, and inverted range corrected.
 * @param {{ startDate?: unknown, endDate?: unknown }} state
 * @param {"startDate"|"endDate"} field
 * @param rawValue from DateInput (null = cleared)
 */
export function applyDashboardRangeField(state, field, rawValue) {
  if (rawValue == null) {
    return { ...state, [field]: null };
  }
  const v = clampDashboardPickerDate(rawValue);
  if (v == null) {
    return state;
  }

  if (field === "startDate") {
    const next = { ...state, startDate: v };
    const endDay = startOfDay(state.endDate);
    if (endDay && endDay.isBefore(dayjs(v).startOf("day"))) {
      next.endDate = v;
    }
    return next;
  }

  const next = { ...state, endDate: v };
  const startDay = startOfDay(state.startDate);
  if (startDay && dayjs(v).startOf("day").isBefore(startDay)) {
    next.endDate = startDay.toDate();
  }
  return next;
}

/**
 * Safe YYYY-MM-DD pair for API / SWR keys: no future days, start <= end.
 */
export function sanitizeDashboardDateRangeForQuery(startDate, endDate) {
  const today = dashboardTodayStart();
  let s = startOfDay(startDate);
  let e = startOfDay(endDate);
  if (s && s.isAfter(today)) s = today;
  if (e && e.isAfter(today)) e = today;
  if (s && e && e.isBefore(s)) e = s;
  return {
    startStr: s ? s.format("YYYY-MM-DD") : null,
    endStr: e ? e.format("YYYY-MM-DD") : null,
  };
}

/**
 * Resolve the effective date range for wallet/payment chart gap-filling.
 * Prefers header picker dates, then API filter metadata, then label bounds.
 */
const readApiFilterDateRange = (apiFilterRange) => {
  const apiFrom = apiFilterRange?.from ?? apiFilterRange?.start_date ?? null;
  const apiTo = apiFilterRange?.to ?? apiFilterRange?.end_date ?? null;
  if (!apiFrom || !apiTo) return null;
  const sanitized = sanitizeDashboardDateRangeForQuery(apiFrom, apiTo);
  if (!sanitized.startStr || !sanitized.endStr) return null;
  return { startDate: sanitized.startStr, endDate: sanitized.endStr };
};

export function resolveOperatorChartDateRange({
  apiFilterRange,
  headerFilters,
  labels = [],
  view,
  preferApiRange = false,
}) {
  if (preferApiRange) {
    const apiRange = readApiFilterDateRange(apiFilterRange);
    if (apiRange) return apiRange;
  }

  const { startStr, endStr } = sanitizeDashboardDateRangeForQuery(
    headerFilters?.startDate,
    headerFilters?.endDate
  );
  if (startStr && endStr) {
    return { startDate: startStr, endDate: endStr };
  }

  const apiRange = readApiFilterDateRange(apiFilterRange);
  if (apiRange) return apiRange;

  const first = labels[0];
  const last = labels[labels.length - 1];
  if (first && last) {
    return { startDate: first, endDate: last };
  }

  const today = dashboardTodayStart();
  if (view === "M") {
    return {
      startDate: today.subtract(11, "month").startOf("month").format("YYYY-MM-DD"),
      endDate: today.format("YYYY-MM-DD"),
    };
  }
  if (view === "W") {
    return {
      startDate: today.subtract(7, "week").startOf("week").format("YYYY-MM-DD"),
      endDate: today.format("YYYY-MM-DD"),
    };
  }

  return {
    startDate: today.subtract(6, "day").format("YYYY-MM-DD"),
    endDate: today.format("YYYY-MM-DD"),
  };
}